import { TalerFormAttributes } from "@gnu-taler/taler-util";
import { InternationalizationAPI } from "../../context/translation.js";
import { DoubleColumnFormDesign, FormMetadata } from "../forms-types.js";
import { Descr } from "./VQF_902_1_customer.js";
import { intervalToDuration, parse, isFuture, isValid } from "date-fns";
import { vqfLanguagePolicy } from "./vqf.js";

export const form_vqf_902_4 = (
  i18n: InternationalizationAPI,
): FormMetadata => ({
  label: i18n.fixed({ en: "Risk Profile AMLA", de: "Risikoprofil GwG" }),
  id: "vqf_902_4",
  version: 1,
  config: VQF_902_4(i18n),
});

export function VQF_902_4(
  i18n: InternationalizationAPI,
): DoubleColumnFormDesign {
  return {
    type: "double-column",
    languagePolicy: vqfLanguagePolicy(i18n),
    sections: [
      {
        title: i18n.fixed({
          en: "Information on customer",
          de: "Angaben zum Kunden",
        }),
        description: Descr.CUSTOMER_INFO_TYPE(i18n),
        fields: [
          {
            id: TalerFormAttributes.CUSTOMER_NAME,
            label: i18n.fixed({ en: "Customer", de: "Kunde" }),
            type: "text",
            required: true,
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Evaluation of politically exposed persons (PEP-Check)",
          de: "Beurteilung politisch exponierter Personen (PEP-Check)",
        }),
        description: i18n.fixed({
          en: "This evaluation has to be completed by all members for every business relationship.",
          de: "Diese Beurteilung ist von allen Mitgliedern für jede Geschäftsbeziehung durchzuführen.",
        }),
        fields: [
          {
            id: TalerFormAttributes.PEP_FOREIGN,
            label: i18n.fixed({ en: "Foreign PEP", de: "Ausländische PEP" }),
            help: i18n.fixed({
              en: "Is the customer, the beneficial owner or the controlling person or authorised representative a foreign PEP or closely related to such a person?",
              de: "Ist der Kunde, der wirtschaftlich Berechtigte, der Kontrollinhaber oder der bevollmächtigte Vertreter eine ausländische PEP oder steht in einer engen Beziehung zu einer solchen Person?",
            }),
            type: "choiceHorizontal",
            required: true,
            choices: [
              {
                value: true,
                label: i18n.fixed({ en: "Yes", de: "Ja" }),
              },
              {
                value: false,
                label: i18n.fixed({ en: "No", de: "Nein" }),
              },
            ],
          },
          {
            id: TalerFormAttributes.PEP_DOMESTIC,
            label: i18n.fixed({ en: "Domestic PEP", de: "Inländische PEP" }),
            help: i18n.fixed({
              en: "Is the customer, the beneficial owner or the controlling person or authorised representative a domestic PEP or closely related to such a person?",
              de: "Ist der Kunde, der wirtschaftlich Berechtigte, der Kontrollinhaber oder der bevollmächtigte Vertreter eine inländische PEP oder steht in einer engen Beziehung zu einer solchen Person?",
            }),
            type: "choiceHorizontal",
            required: true,
            choices: [
              {
                value: true,
                label: i18n.fixed({ en: "Yes", de: "Ja" }),
              },
              {
                value: false,
                label: i18n.fixed({ en: "No", de: "Nein" }),
              },
            ],
          },
          {
            id: TalerFormAttributes.PEP_INTERNATIONAL_ORGANIZATION,
            label: i18n.fixed({
              en: "PEP of International Organisations",
              de: "PEP internationaler Organisationen",
            }),
            help: i18n.fixed({
              en: "Is the customer, the beneficial owner or the controlling person or authorised representative a PEP in International Organisations or closely related to such a person?",
              de: "Ist der Kunde, der wirtschaftlich Berechtigte, der Kontrollinhaber oder der bevollmächtigte Vertreter eine PEP einer internationalen Organisation oder steht in einer engen Beziehung zu einer solchen Person?",
            }),
            type: "choiceHorizontal",
            required: true,
            choices: [
              {
                value: true,
                label: i18n.fixed({ en: "Yes", de: "Ja" }),
              },
              {
                value: false,
                label: i18n.fixed({ en: "No", de: "Nein" }),
              },
            ],
          },
          {
            id: TalerFormAttributes.PEP_ACCEPTANCE_DATE,
            label: i18n.fixed({
              en: "Acceptance date",
              de: "Datum der Annahme",
            }),
            help: i18n.fixed({
              en: "When the decision of the Senior executive body on the acceptance of a business relationship with a PEP was obtained on.",
              de: "Datum, an dem der Entscheid des obersten Geschäftsführungsorgans über die Annahme einer Geschäftsbeziehung mit einer PEP eingeholt wurde.",
            }),
            type: "isoDateText",
            placeholder: "dd/MM/yyyy",
            pattern: "dd/MM/yyyy",
            required: true,
            validator(text, form) {
              //FIXME: why returning in this format even if pattern is in another?
              const time = parse(text, "yyyy-MM-dd", new Date());
              if (!isValid(time)) {
                return i18n.fixed({
                  en: "invalid format",
                  de: "ungültiges Format",
                });
              }
              if (isFuture(time)) {
                return i18n.fixed({
                  en: "it can't be in the future",
                  de: "darf nicht in der Zukunft liegen",
                });
              }
              const { years } = intervalToDuration({
                start: time,
                end: new Date(),
              });
              if (years && years > 120) {
                return i18n.fixed({
                  en: "it can't be greater than 120 years",
                  de: "darf nicht größer als 120 Jahre sein",
                });
              }
              return undefined;
            },
            hide(value, root) {
              return !(
                root[TalerFormAttributes.PEP_FOREIGN] ||
                root[TalerFormAttributes.PEP_DOMESTIC] ||
                root[TalerFormAttributes.PEP_INTERNATIONAL_ORGANIZATION]
              );
            },
          },
        ],
      },
      {
        title: i18n.fixed({
          en: 'Evaluation "high risk" or non-cooperative country',
          de: "Beurteilung „Hochrisikoland“ oder nicht kooperatives Land",
        }),
        description: i18n.fixed({
          en: "This evaluation has to be completed by all members for every business relationship.",
          de: "Diese Beurteilung ist von allen Mitgliedern für jede Geschäftsbeziehung durchzuführen.",
        }),
        fields: [
          {
            id: TalerFormAttributes.HIGH_RISK_COUNTRY,
            label: i18n.fixed({
              en: "High-risk or non-cooperative country",
              de: "Hochrisikoland oder nicht kooperatives Land",
            }),
            help: i18n.fixed({
              en: "Is the customer, the beneficial owner or the controlling person or authorised representative in a country considered by the FATF as high-risk or non-cooperative and for which FATF requires increased diligence?",
              de: "Befindet sich der Kunde, der wirtschaftlich Berechtigte, der Kontrollinhaber oder der bevollmächtigte Vertreter in einem Land, das von der FATF als Hochrisikoland oder nicht kooperatives Land eingestuft wird und für welches die FATF erhöhte Sorgfaltspflichten verlangt?",
            }),
            type: "choiceHorizontal",
            required: true,
            choices: [
              {
                value: true,
                label: i18n.fixed({ en: "Yes", de: "Ja" }),
              },
              {
                value: false,
                label: i18n.fixed({ en: "No", de: "Nein" }),
              },
            ],
          },
          {
            id: TalerFormAttributes.HIGH_RISK_ACCEPTANCE_DATE,
            label: i18n.fixed({
              en: "Acceptance date",
              de: "Datum der Annahme",
            }),
            help: i18n.fixed({
              en: "When the decision of the Senior executive body on the acceptance of such a business relationship was obtained on.",
              de: "Datum, an dem der Entscheid des obersten Geschäftsführungsorgans über die Annahme einer solchen Geschäftsbeziehung eingeholt wurde.",
            }),
            type: "isoDateText",
            placeholder: "dd/MM/yyyy",
            pattern: "dd/MM/yyyy",
            required: true,
            validator(text, form) {
              //FIXME: why returning in this format even if pattern is in another?
              const time = parse(text, "yyyy-MM-dd", new Date());
              if (!isValid(time)) {
                return i18n.fixed({
                  en: "invalid format",
                  de: "ungültiges Format",
                });
              }
              if (isFuture(time)) {
                return i18n.fixed({
                  en: "it can't be in the future",
                  de: "darf nicht in der Zukunft liegen",
                });
              }
              const { years } = intervalToDuration({
                start: time,
                end: new Date(),
              });
              if (years && years > 120) {
                return i18n.fixed({
                  en: "it can't be greater than 120 years",
                  de: "darf nicht größer als 120 Jahre sein",
                });
              }
              return undefined;
            },

            hide(value, root) {
              return !root[TalerFormAttributes.HIGH_RISK_COUNTRY];
            },
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Evaluation of business relationship risk",
          de: "Beurteilung des Risikos der Geschäftsbeziehung",
        }),
        fields: [
          {
            type: "caption",
            label:
              "This evaluation has to be completed by all members who have in total more than 20 customers for every business relationship. At least two risk categories have to be chosen and assessed.",
          },
        ],
      },
      {
        title: i18n.fixed({ en: "Country risk", de: "Länderrisiko" }),
        fields: [
          {
            id: TalerFormAttributes.COUNTRY_RISK_NATIONALITY_TYPE,
            label: i18n.fixed({
              en: "Applicable country risk types",
              de: "Anwendbare Arten von Länderrisiken",
            }),
            choices: [
              {
                label: i18n.fixed({
                  en: "Nationality of the customer",
                  de: "Staatsangehörigkeit des Kunden",
                }),
                value: "NATIONALITY_CUSTOMER",
              },
              {
                label: i18n.fixed({
                  en: "Nationality of the beneficial owner of the assets",
                  de: "Staatsangehörigkeit des wirtschaftlich Berechtigten der Vermögenswerte",
                }),
                value: "NATIONALITY_OWNER",
              },
              {
                label: i18n.fixed({
                  en: "Domicile/residential address of the customer",
                  de: "Sitz/Wohnsitzadresse des Kunden",
                }),
                value: "DOMICILE_CUSTOMER",
              },
              {
                label: i18n.fixed({
                  en: "Domicile/residential address of the beneficial owner of the assets",
                  de: "Sitz/Wohnsitzadresse des wirtschaftlich Berechtigten der Vermögenswerte",
                }),
                value: "DOMICILE_OWNER",
              },
              {
                label: i18n.fixed({
                  en: "Domicile/residential address of the controlling person",
                  de: "Sitz/Wohnsitzadresse des Kontrollinhabers",
                }),
                value: "DOMICILE_CONTROLLING",
              },
            ],
            type: "selectMultiple",
            required: false,
          },
          {
            id: TalerFormAttributes.COUNTRY_RISK_NATIONALITY_LEVEL,
            label: i18n.fixed({
              en: "Country risk level (nationality)",
              de: "Länderrisikostufe (Staatsangehörigkeit)",
            }),
            help: i18n.fixed({
              en: "Risk category according to VQF country list (VQF doc. no. 902.4.1)",
              de: "Risikokategorie gemäss VQF-Länderliste (VQF-Dok. Nr. 902.4.1)",
            }),
            choices: [
              {
                label: i18n.fixed({
                  en: "Low (Risk 0)",
                  de: "Niedrig (Risiko 0)",
                }),
                value: "LOW",
                description: i18n.fixed({
                  en: "Risk 0 acc. to VQF country list (VQF doc. no. 902.4.1)",
                  de: "Risiko 0 gemäss VQF-Länderliste (VQF-Dok. Nr. 902.4.1)",
                }),
              },
              {
                label: i18n.fixed({
                  en: "Medium (Risk 1)",
                  de: "Mittel (Risiko 1)",
                }),
                value: "MEDIUM",
                description: i18n.fixed({
                  en: "Risk 1 acc. to VQF country list (VQF doc. no. 902.4.1)",
                  de: "Risiko 1 gemäss VQF-Länderliste (VQF-Dok. Nr. 902.4.1)",
                }),
              },
              {
                label: i18n.fixed({
                  en: "High (Risk 2)",
                  de: "Hoch (Risiko 2)",
                }),
                value: "HIGH",
                description: i18n.fixed({
                  en: "Risk 2 acc. to VQF country list (VQF doc. no. 902.4.1)",
                  de: "Risiko 2 gemäss VQF-Länderliste (VQF-Dok. Nr. 902.4.1)",
                }),
              },
            ],
            type: "choiceHorizontal",
            required: false,
          },
          {
            id: TalerFormAttributes.COUNTRY_RISK_BUSINESS_TYPE,
            label: i18n.fixed({
              en: "Country risk type (place of business activity)",
              de: "Art des Länderrisikos (Ort der Geschäftstätigkeit)",
            }),
            choices: [
              {
                label: i18n.fixed({ en: "Customer", de: "Kunde" }),
                value: "CUSTOMER",
              },
              {
                label: i18n.fixed({
                  en: "Beneficial owner",
                  de: "Wirtschaftlich Berechtigte(r)",
                }),
                value: "OWNER",
              },
            ],
            type: "selectMultiple",
            required: false,
          },
          {
            id: TalerFormAttributes.COUNTRY_RISK_BUSINESS_LEVEL,
            label: i18n.fixed({
              en: "Country risk level (business activity)",
              de: "Länderrisikostufe (Geschäftstätigkeit)",
            }),
            help: i18n.fixed({
              en: "Risk category according to VQF country list (VQF doc. no. 902.4.1)",
              de: "Risikokategorie gemäss VQF-Länderliste (VQF-Dok. Nr. 902.4.1)",
            }),
            choices: [
              {
                label: i18n.fixed({
                  en: "Low (Risk 0)",
                  de: "Niedrig (Risiko 0)",
                }),
                value: "LOW",
                description: i18n.fixed({
                  en: "Risk 0 acc. to VQF country list (VQF doc. no. 902.4.1)",
                  de: "Risiko 0 gemäss VQF-Länderliste (VQF-Dok. Nr. 902.4.1)",
                }),
              },
              {
                label: i18n.fixed({
                  en: "Medium (Risk 1)",
                  de: "Mittel (Risiko 1)",
                }),
                value: "MEDIUM",
                description: i18n.fixed({
                  en: "Risk 1 acc. to VQF country list (VQF doc. no. 902.4.1)",
                  de: "Risiko 1 gemäss VQF-Länderliste (VQF-Dok. Nr. 902.4.1)",
                }),
              },
              {
                label: i18n.fixed({
                  en: "High (Risk 2)",
                  de: "Hoch (Risiko 2)",
                }),
                value: "HIGH",
                description: i18n.fixed({
                  en: "Risk 2 acc. to VQF country list (VQF doc. no. 902.4.1)",
                  de: "Risiko 2 gemäss VQF-Länderliste (VQF-Dok. Nr. 902.4.1)",
                }),
              },
            ],
            type: "choiceHorizontal",
          },
        ],
      },
      {
        title: i18n.fixed({ en: "Industry risk", de: "Branchenrisiko" }),
        fields: [
          {
            id: TalerFormAttributes.INDUSTRY_RISK_TYPE,
            label: i18n.fixed({
              en: "Industry risk source",
              de: "Quelle des Branchenrisikos",
            }),
            type: "selectMultiple",
            choices: [
              {
                label: i18n.fixed({ en: "Customer", de: "Kunde" }),
                value: "CUSTOMER",
              },
              {
                label: i18n.fixed({
                  en: "Beneficial owner of the assets",
                  de: "Wirtschaftlich Berechtigter der Vermögenswerte",
                }),
                value: "OWNER",
              },
            ],
            required: false,
          },
          {
            id: TalerFormAttributes.INDUSTRY_RISK_LEVEL,
            label: i18n.fixed({
              en: "Industry risk level",
              de: "Branchenrisikostufe",
            }),
            type: "choiceStacked",
            choices: [
              {
                label: i18n.fixed({
                  en: "Transparent (Risk Level 0)",
                  de: "Transparent (Risikostufe 0)",
                }),
                description: i18n.fixed({
                  en: "Clearly defined, transparent, easily comprehensible business activity well known to the member.",
                  de: "Klar definierte, transparente, leicht nachvollziehbare Geschäftstätigkeit, die dem Mitglied gut bekannt ist.",
                }),
                value: "TRANSPARENT",
              },
              {
                label: i18n.fixed({
                  en: "High cash transactions (Risk Level 1)",
                  de: "Hoher Anteil an Bargeldtransaktionen (Risikostufe 1)",
                }),
                description: i18n.fixed({
                  en: "Business activity with a high level of cash transactions.",
                  de: "Geschäftstätigkeit mit einem hohen Anteil an Bartransaktionen.",
                }),
                value: "HIGH_CASH_TRANSACTION",
              },
              {
                label: i18n.fixed({
                  en: "Not well known (Risk Level 1)",
                  de: "Nicht gut bekannt (Risikostufe 1)",
                }),
                description: i18n.fixed({
                  en: "Business activity not well known to the member.",
                  de: "Geschäftstätigkeit, die dem Mitglied nicht gut bekannt ist.",
                }),
                value: "NOT_WELL_KNOWN",
              },
              {
                label: i18n.fixed({
                  en: "High-risk trade (Risk Level 2)",
                  de: "Handel mit hohem Risiko (Risikostufe 2)",
                }),
                description: i18n.fixed({
                  en: "Trade in munitions/arms, raw gem stones/diamonds, jewellery, international trade in exotic animals, casino and lottery business, trade in erotic wares.",
                  de: "Handel mit Munition/Waffen, Rohedelsteinen/Diamanten, Schmuck, internationaler Handel mit exotischen Tieren, Casino- und Lotteriegeschäft, Handel mit Erotikartikeln.",
                }),
                value: "HIGH_RISK_TRADE",
              },
              {
                label: i18n.fixed({
                  en: "Unknown industry (Risk Level 2)",
                  de: "Unbekannte Branche (Risikostufe 2)",
                }),
                description: i18n.fixed({
                  en: "Member has no personal knowledge of the customer’s industry.",
                  de: "Das Mitglied verfügt über keine persönliche Kenntnis der Branche des Kunden.",
                }),
                value: "UNKNOWN_INDUSTRY",
              },
            ],
            required: false,
          },
        ],
      },
      {
        title: i18n.fixed({ en: "Contact risk", de: "Kontaktrisiko" }),
        description: i18n.fixed({
          en: "Type of contact to the customer/beneficial owner of the assets.",
          de: "Art des Kontakts zum Kunden/wirtschaftlich Berechtigten der Vermögenswerte.",
        }),
        fields: [
          {
            id: TalerFormAttributes.CONTACT_RISK_LEVEL,
            label: i18n.fixed({
              en: "Contact risk level",
              de: "Kontaktrisikostufe",
            }),
            choices: [
              {
                label: i18n.fixed({
                  en: "Low contact risk",
                  de: "Tiefes Kontaktrisiko",
                }),
                description: i18n.fixed({
                  en: "Personal acquaintance between member and customer/beneficial owner of the assets over several years (at least 2) prior to entering into the business relationship",
                  de: "Persönliche Bekanntschaft zwischen Mitglied und Kunde/wirtschaftlich Berechtigtem der Vermögenswerte über mehrere Jahre (mindestens 2) vor Aufnahme der Geschäftsbeziehung",
                }),
                value: "LOW",
              },
              {
                label: i18n.fixed({
                  en: "Medium contact risk",
                  de: "Mittleres Kontaktrisiko",
                }),
                description: i18n.fixed({
                  en: "The customer/beneficial owner was not personally known to the member for several years (at least 2) prior to entering into the business relationship; however (a) no business was entered into in the absence of the customer/beneficial owner, or (b) the customer was at least introduced/brokered by a trusted third party.",
                  de: "Der Kunde/wirtschaftlich Berechtigte war dem Mitglied nicht während mehrerer Jahre (mindestens 2) vor Aufnahme der Geschäftsbeziehung persönlich bekannt; jedoch wurde (a) keine Geschäftsbeziehung in Abwesenheit des Kunden/wirtschaftlich Berechtigten eingegangen, oder (b) der Kunde wurde zumindest von einem vertrauenswürdigen Dritten eingeführt/vermittelt.",
                }),
                value: "MEDIUM",
              },
              {
                label: i18n.fixed({
                  en: "High contact risk",
                  de: "Hohes Kontaktrisiko",
                }),
                description: i18n.fixed({
                  en: "The customer/beneficial owner was not personally known to the member and business was entered into in the absence of the former (relationship by correspondence) and the customer was not introduced/brokered by a trusted third party.",
                  de: "Der Kunde/wirtschaftlich Berechtigte war dem Mitglied nicht persönlich bekannt, und die Geschäftsbeziehung wurde in dessen Abwesenheit eingegangen (Korrespondenzbeziehung), wobei der Kunde nicht von einem vertrauenswürdigen Dritten eingeführt/vermittelt wurde.",
                }),
                value: "HIGH",
              },
            ],
            type: "choiceStacked",
            required: false,
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Summary evaluation",
          de: "Zusammenfassende Beurteilung",
        }),
        fields: [
          {
            id: TalerFormAttributes.RISK_RATIONALE,
            label: i18n.fixed({
              en: "Justification for risk assessment",
              de: "Begründung der Risikobeurteilung",
            }),
            type: "textArea",
            required: false,
          },
          {
            id: TalerFormAttributes.RISK_CLASSIFICATION_LEVEL,
            label: i18n.fixed({
              en: "Risk classification",
              de: "Risikoklassifizierung",
            }),
            help: i18n.fixed({
              en: "Conclusion whether the business relationship is with or without increased risk.",
              de: "Schlussfolgerung, ob die Geschäftsbeziehung mit oder ohne erhöhtes Risiko verbunden ist.",
            }),
            choices: [
              {
                label: i18n.fixed({
                  en: "No high risk",
                  de: "Kein hohes Risiko",
                }),
                value: "NO_HIGH_RISK",
              },
              {
                label: i18n.fixed({ en: "High risk", de: "Hohes Risiko" }),
                value: "HIGH_RISK",
              },
            ],
            type: "choiceHorizontal",
            required: false,
          },
          {
            id: TalerFormAttributes.RISK_ACCEPTANCE_DATE,
            label: i18n.fixed({
              en: "Acceptance date",
              de: "Datum der Annahme",
            }),
            help: i18n.fixed({
              en: "When the decision of the Senior executive body on the acceptance of a business relationship with increased risk was obtained on.",
              de: "Datum, an dem der Entscheid des obersten Geschäftsführungsorgans über die Annahme einer Geschäftsbeziehung mit erhöhtem Risiko eingeholt wurde.",
            }),
            type: "isoDateText",
            placeholder: "dd/MM/yyyy",
            pattern: "dd/MM/yyyy",
            required: true,
            validator(text, form) {
              //FIXME: why returning in this format even if pattern is in another?
              const time = parse(text, "yyyy-MM-dd", new Date());
              if (!isValid(time)) {
                return i18n.fixed({
                  en: "invalid format",
                  de: "ungültiges Format",
                });
              }
              if (isFuture(time)) {
                return i18n.fixed({
                  en: "it can't be in the future",
                  de: "darf nicht in der Zukunft liegen",
                });
              }
              const { years } = intervalToDuration({
                start: time,
                end: new Date(),
              });
              if (years && years > 120) {
                return i18n.fixed({
                  en: "it can't be greater than 120 years",
                  de: "darf nicht größer als 120 Jahre sein",
                });
              }
              return undefined;
            },
            hide(value, root) {
              return (
                root[TalerFormAttributes.RISK_CLASSIFICATION_LEVEL] !=
                "HIGH_RISK"
              );
            },
          },
        ],
      },
    ],
  };
}
